import { Injectable, inject } from '@angular/core';

import { OfferSource } from '@cds';
import {
    EventModel,
    EventOption,
    EventOptionGroup,
    EventParticipant,
    EventParticipantImage,
    Tv2MarketParameters,
} from '@frontend/sports/betting-offer/feature/model';
import { ImagesConfig, OutrightsGridConfig } from '@frontend/sports/common/client-config-data-access';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { formatString } from '@frontend/sports/common/core/utils/string';
import { ParticipantImageFormat } from '@frontend/sports/types/components/outrights-grid';
import { some } from 'lodash-es';

export const emptyImage: EventParticipantImage = {};

export const isPlayerMatched = (players: EventParticipant[], options: EventOption[]): boolean =>
    some(options, (option) => some(players, (player) => player.name === option.name || player.shortName === option.name));

@Injectable({ providedIn: 'root' })
export class ParticipantImageService {
    private outrightsGridConfig = inject(OutrightsGridConfig);
    private imageConfig = inject(ImagesConfig);

    optionImages: NumberDictionary<EventParticipantImage> = {};
    enableOptionImages: boolean;

    showOrHideOptionImages(
        options: EventOption[],
        sportId: number | undefined,
        offerSource: OfferSource | undefined,
    ): [boolean, NumberDictionary<EventParticipantImage>] {
        this.optionImages = {};
        this.enableOptionImages =
            !!sportId &&
            this.outrightsGridConfig.enableParticipantImages.includes(sportId) &&
            options.some((option) => (option.teamId ? !!option.teamId : !!option.playerId));
        if (this.enableOptionImages) {
            options.map((option) => (this.optionImages[option.id] = this.getParticipantImage(option, sportId, offerSource)));
        }

        return [this.enableOptionImages, this.optionImages];
    }

    getPlayerJersey(event: EventModel, optionGroup: EventOptionGroup): EventOption[] {
        if (!optionGroup?.options) {
            return [];
        }

        return optionGroup.options.map((option) => {
            // eslint-disable-next-line no-param-reassign
            option.playerId = option.playerId || option.optionPlayerId;

            if (option.playerId) {
                // eslint-disable-next-line no-param-reassign
                option.teamId = this.getTeamId(event.players, option.playerId) ?? this.getTeamId(event.participants, option.playerId);
                // eslint-disable-next-line no-param-reassign
                option.image =
                    event.players.find((player) => player.id === option.teamId)?.image ??
                    event.participants.find((participant) => participant.id === option.teamId)?.image;
            }

            return option;
        });
    }

    private getTeamId(participants: EventParticipant[], optionPlayerId: number): number | undefined {
        const participant = participants.find((p) => p.fixtureParticipantId === optionPlayerId);

        return participant?.teamId ?? participant?.id;
    }

    getParticipantImage(
        option: EventOption,
        sportId: number | undefined,
        offerSource: OfferSource | undefined,
        showHomeAndAwayJerseys?: boolean,
    ): EventParticipantImage {
        if (!option.playerId) {
            return {};
        }
        const overrideFormat = this.outrightsGridConfig.overrideParticipantImageNameFormat.find((format) => format.sportId === sportId);
        const playerId = option.playerId.toString();
        const teamId = option.teamId?.toString();

        return {
            isParticipantProfile: option?.image && option?.image?.isParticipantProfile,
            logo: !teamId
                ? option?.image && option?.image?.isParticipantProfile
                    ? option.image?.logo
                    : formatString(overrideFormat?.logoNameFormat || '{0}_logo', playerId)
                : undefined,
            jersey:
                option?.image && option?.image?.isParticipantProfile
                    ? option.image?.jersey
                    : this.getJerseyName(teamId || playerId, offerSource, option, overrideFormat, showHomeAndAwayJerseys),
        };
    }

    private getJerseyName(
        playerId: string,
        offerSource: OfferSource | undefined,
        option: EventOption,
        overrideFormat?: ParticipantImageFormat,
        showHomeAndAwayJerseys?: boolean,
    ): string {
        let jerseyNameFormat = overrideFormat?.jerseyNameFormat || '{0}_1';
        if (offerSource === OfferSource.V2) {
            if (showHomeAndAwayJerseys) {
                jerseyNameFormat = option.image?.jersey || `2_${jerseyNameFormat}`;
            } else {
                jerseyNameFormat = `2_${jerseyNameFormat}`;
            }
        }

        return formatString(jerseyNameFormat, playerId);
    }

    showOptionsImage(event: EventModel, optionGroup: EventOptionGroup): boolean {
        const marketType = optionGroup.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value;

        return (
            (event.offerSource === OfferSource.V2 &&
                this.imageConfig.isParticipantImageEnabled &&
                marketType !== undefined &&
                this.imageConfig.participantImageEnabledMarketTypes[event.sport?.id]?.includes(marketType)) ??
            false
        );
    }

    showHeadshotImage(sportId: number, optionGroup: string): boolean {
        return (!!sportId && this.imageConfig.headshotEnabledOptionGroups[sportId]?.includes(optionGroup)) || false;
    }
}
